import {Component, Input} from '@angular/core';
import {SupportContact} from '@models/adminstration/organization/support-contact/support-contact';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {ContactsService} from '@services/administration/organization/configuration/contacts/contacts.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {ContactModalComponent} from '@components/administration/organization/configuration/contacts/contact-modal/contact-modal.component';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-organization-support-contact-actions',
    templateUrl: './contact-actions.component.html',
    styleUrls: ['./contact-actions.component.scss'],
    standalone: false
})
export class ContactActionsComponent {
  private COMPONENT_NAME: string = 'ContactActionsComponent';

  @Input()
  organizationId: number = -1;

  @Input()
  contacts: SupportContact[] | undefined;

  @Input()
  contact: SupportContact = new SupportContact();

  @Input()
  isAdminRole: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;


  constructor(private modalService: ModalService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private contactsService: ContactsService) {
  }

  onDeleteContact(contact: SupportContact | undefined) {
    if (contact == undefined || contact.id == undefined || this.organizationId == -1) {
      return;
    }
    const removeMessageTitle = this.translate.instant('Delete support contact');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete the support contact %s ?').replace('%s', contact.name.toBold());
    const removeMessageOK = this.translate.instant('Delete contact');
    const removeMessageCancel = this.translate.instant('Cancel');

    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = removeMessageTitle;
    modalRef.componentInstance.body = removeMessageBody;
    modalRef.componentInstance.cancelButtonText = removeMessageCancel;
    modalRef.componentInstance.okButtonText = removeMessageOK;

    modalRef.result.then(() => {}, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      this.loadingIndicator.show(this.translate.instant('Deleting contact'));
      this.contactsService.deleteContact(this.organizationId, contact.id).subscribe((success) => {
        if (success) {
          const index = this.contacts?.indexOf(contact);
          if (index != undefined && index > -1) {
            this.contacts?.splice(index, 1);
          }
          const message = this.translate.instant('The support contact: "%s" was deleted.').replace('%s', contact.name);
          this.notification.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        } else {
          this.notification.warning(
              this.translate.instant('Cannot delete the support contact') + ': ' +
              contact.name, this.COMPONENT_NAME);
        }

      }, (error) => {
        this.notification.error(
            this.translate.instant('There was an error deleting support contact') + ': ' +
            contact.name, error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
      });
    });
  }

  onEditContact(contact: SupportContact) {
    const contactBackup = this.contactsService.copyContactProperties(contact, null);

    const modalRef = this.modalService.open(ContactModalComponent, {size: 'lg'});
    modalRef.componentInstance.contact = contact;
    modalRef.result.then((result) => {
      this.contactsService.saveContact(this.organizationId, result).subscribe((success) => {
        this.contact = this.contactsService.copyContactProperties(success, this.contact);
        const message = this.translate.instant('Support contact') + ' ' + success.name + ' ' +
            this.translate.instant('saved') + '.';

        this.notification.success(message, this.COMPONENT_NAME, false);
      }, (error) => {
        this.notification.error(this.translate.instant('Error saving support contact') + ': ' + result.name,
            error, this.COMPONENT_NAME, true);
        this.contact = this.contactsService.copyContactProperties(contactBackup, this.contact);
      });
    }, () => {
      this.contact = this.contactsService.copyContactProperties(contactBackup, this.contact);

    });
  }

}
